/*
 * New header components (header-component, hover-disclosure) required by the
 * rebuilt sections/header.liquid, snippets/header-block.liquid and
 * snippets/nav-item.liquid. Kept isolated from theme.js/theme.dev.js on purpose
 * so the rest of the site's JS is untouched.
 */
(function () {
  if (!customElements.get('header-component')) {
    customElements.define(
      'header-component',
      class HeaderComponent extends HTMLElement {
        constructor() {
          super();
          this.background = document.querySelector('[data-header-background]');
          this.headerWrapper = this.querySelector('[data-wrapper]');
          this.logoTextLink = this.querySelector('[data-logo-text-link]');
          this.logoElement = this.querySelector('[data-logo]');
          this.nav = this.querySelector('[data-nav]');
          this.mobileMenuTrigger = this.querySelector('[data-mobile-menu-trigger]');
          this.headerStateEvent = this.headerState.bind(this);
          this.handleTouchstartEvent = this.handleTouchstart.bind(this);
          this.updateBackgroundHeightEvent = this.updateBackgroundHeight.bind(this);
          this.triggerDrawerOpeningEvent = () => this.triggerDrawerOpening(this.mobileMenuTrigger);
        }

        connectedCallback() {
          if (this.classList.contains('js__header__clone')) return;

          this.initTransparentHeader();

          this.listenWidth();
          this.initMobileNavToggle();
          this.handleTextLinkLogos();
          this.initStickyHeader();
          this.handleBackgroundEvents();

          document.body.addEventListener('touchstart', this.handleTouchstartEvent, {passive: true});
          this.updateHeaderHover();
          this.updateLocalizationOverlap();
        }

        initTransparentHeader() {
          const isTransparent = this.dataset.transparent === 'true';
          const firstSection = document.querySelector('[data-main]').children[0];

          if (!firstSection) return;

          const preventTransparentHeader = firstSection.querySelector('[data-prevent-transparent-header]:first-of-type');

          window.isHeaderTransparent = isTransparent && firstSection.classList.contains('supports-transparent-header') && !preventTransparentHeader;
        }

        getMinWidth() {
          const headerWrapperStyles = this.headerWrapper.currentStyle || window.getComputedStyle(this.headerWrapper);
          const headerPaddings = parseInt(headerWrapperStyles.paddingLeft) * 2;
          const comparitor = this.cloneNode(true);
          comparitor.classList.add('js__header__clone');
          // ponytail: always measure the uncompressed layout, otherwise a clone
          // taken while already compressed reads 0-width hidden nav items and
          // undercounts, flip-flopping the compress class every resize tick.
          comparitor.classList.remove('site-header--compress');
          document.body.appendChild(comparitor);
          const wideElements = comparitor.querySelectorAll('[data-takes-space]');
          const navAlignment = this.getAttribute('data-nav-alignment');
          const minWidth = this._sumSplitWidths(wideElements, navAlignment);

          document.body.removeChild(comparitor);

          return minWidth + wideElements.length * 20 + headerPaddings;
        }

        listenWidth() {
          // ponytail: assets/theme.js still ships the original (pre-rebuild)
          // Header controller, auto-instantiated on this same
          // [data-section-type="header"] element (kept only because its
          // constructor also sets window.cart, which cart-drawer-new.js
          // still falls back to — can't remove it here). It listens to the
          // same 'theme:resize' event but computes its min-width ONCE at
          // construction and never again, and it registers after us, so on
          // a real resize its stale one-time check can run right after ours
          // and stomp our correct class. Deferring our recompute a frame
          // guarantees we always run last and win, deterministically.
          this.checkWidthEvent = () => requestAnimationFrame(() => this.checkWidth());

          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(this.checkWidthEvent);
            this.resizeObserver.observe(this);
          }

          document.addEventListener('theme:resize', this.checkWidthEvent);

          this.checkWidth();

          // ponytail: the first checkWidth() can run before web fonts swap in,
          // measuring nav text at fallback-font widths. ResizeObserver only
          // watches the header's own box, which doesn't change when just its
          // text reflows, so a bad initial read never self-corrects until a
          // real resize. Re-measure once fonts are actually ready.
          document.fonts?.ready.then(this.checkWidthEvent);
        }

        checkWidth() {
          if (this.isResizing || !this.isConnected) return;
          this.isResizing = true;
          const shouldCompress = window.innerWidth < this.getMinWidth();
          const hasCompress = this.classList.contains('site-header--compress');

          if (shouldCompress && !hasCompress) {
            this.classList.add('site-header--compress');
          } else if (!shouldCompress && hasCompress) {
            this.classList.remove('site-header--compress');
          }

          this.updateLocalizationOverlap();
          this.isResizing = false;
        }

        updateLocalizationOverlap() {
          const localizationForm = this.headerWrapper?.querySelector('.localization-form');
          if (!localizationForm || !this.logoElement) return;

          localizationForm.classList.remove('localization-form--hidden');

          const logoRect = this.logoElement.getBoundingClientRect();
          const formRect = localizationForm.getBoundingClientRect();

          if (this.rectsOverlap(logoRect, formRect)) {
            localizationForm.classList.add('localization-form--hidden');
          }
        }

        updateHeaderHover() {
          requestAnimationFrame(() => {
            const isHovered = this.matches(':hover');
            const hasHoveredClass = this.classList.contains('site-header--hovered');

            if (isHovered && !hasHoveredClass) this.classList.add('site-header--hovered');
          });
        }

        handleTouchstart(event) {
          const isInHeader = this.contains(event.target);
          const activeNavItem = this.querySelector('.is-visible[data-nav-item]');

          if (!isInHeader && activeNavItem) {
            activeNavItem.dispatchEvent(new Event('mouseleave', {bubbles: true}));
          }
        }

        handleTextLinkLogos() {
          if (this.logoTextLink === null) return;

          const headerHeight = this.offsetHeight;
          document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
          document.documentElement.style.setProperty('--header-sticky-height', `${headerHeight}px`);
        }

        initStickyHeader() {
          this.headerSticky = this.hasAttribute('data-header-sticky');
          this.hasScrolled = false;
          this.position = this.dataset.position;

          if (this.position === 'fixed') {
            this.headerState();
            document.addEventListener('theme:scroll', this.headerStateEvent);
            return;
          }

          document.body.classList.remove('has-scrolled');
          if (window.isHeaderTransparent) {
            this.classList.add('site-header--transparent');
          }
        }

        // Switch to "compact" header on scroll
        headerState(event) {
          const headerHeight = parseInt(this.dataset.height || this.offsetHeight);
          const announcementBar = document.querySelector('[data-announcement-wrapper]');
          const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
          const countdownTimer = document.querySelector('[data-countdown-timer]');
          const countdownHeight = countdownTimer ? countdownTimer.offsetHeight : 0;
          const pageOffset = headerHeight + announcementHeight + countdownHeight;
          const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollUp = event && event.detail && event.detail.up;

          // Show compact header when scroll down
          this.hasScrolled = currentScrollTop > pageOffset;
          document.body.classList.toggle('has-scrolled', this.hasScrolled);

          // Hide compact header when scroll back to top
          const hideHeaderThreshold = pageOffset + window.stickyHeaderHeight;
          const bellowThreshold = currentScrollTop < hideHeaderThreshold;
          const shouldHideHeader = bellowThreshold && scrollUp;
          document.body.classList.toggle('hide-header', shouldHideHeader);

          if (window.isHeaderTransparent) {
            const shouldShowTransparentHeader = !this.hasScrolled || shouldHideHeader;
            this.classList.toggle('site-header--transparent', shouldShowTransparentHeader);
          }

          // Update header background height if users scroll the page with their mouse over the header or over an opened nav menu
          if (this.classList.contains('site-header--hovered')) {
            const activeNavItem = this.querySelector('.is-visible[data-nav-item]');

            if (activeNavItem != null) {
              activeNavItem.dispatchEvent(new Event('mouseenter', {bubbles: true}));
            } else {
              const currentHeight = this.hasScrolled ? window.stickyHeaderHeight : headerHeight;
              this.background.style.setProperty('--header-background-height', `${currentHeight}px`);
            }
          }
        }

        handleBackgroundEvents() {
          this.headerWrapper.addEventListener('mouseenter', this.updateBackgroundHeightEvent);

          this.headerWrapper.addEventListener('mouseleave', this.updateBackgroundHeightEvent);

          this.addEventListener('focusout', this.updateBackgroundHeightEvent);

          document.addEventListener('theme:cart:close', this.updateBackgroundHeightEvent);

          // Helps fixing Safari issues with background not being updated on search close and mouse over the header
          document.addEventListener('theme:search:close', this.updateBackgroundHeightEvent);
        }

        updateBackgroundHeight(event) {
          const isDesktop = matchMedia('(pointer:fine)').matches;
          const isFocusEnabled = !document.body.classList.contains('no-outline');
          const isNotTabbingOnDesktop = isDesktop && !isFocusEnabled;

          if (!event) return;

          let drawersVisible = ['js-drawer-open', 'js-drawer-open-cart', 'js-quick-view-visible', 'js-quick-view-from-cart'].some((popupClass) => document.body.classList.contains(popupClass));

          // Update header background height on:
          // 'mouseenter' event
          // opened Cart drawer/Quick View/Menu drawers
          if (event.type === 'mouseenter' || drawersVisible) {
            this.headerHeight = this.hasScrolled ? window.stickyHeaderHeight : this.offsetHeight;
            this.classList.add('site-header--hovered');

            if (!this.classList.contains('site-header--menu-opened')) {
              this.background.style.setProperty('--header-background-height', `${this.headerHeight}px`);
            }
          }

          if (event.type === 'mouseenter') return;

          requestAnimationFrame(() => {
            drawersVisible = ['js-drawer-open', 'js-drawer-open-cart', 'js-quick-view-visible', 'js-quick-view-from-cart'].some((popupClass) => document.body.classList.contains(popupClass));

            if (drawersVisible) return;

            // Remove header background and handle focus on:
            // 'mouseleave' event
            // 'theme:cart:close' event
            // 'theme:search:close' event
            // 'focusout' event
            // closed Cart drawer/Quick View/Menu drawers

            if (event.type === 'focusout' && !isDesktop) return;
            if (event.type === 'theme:search:close' && !isNotTabbingOnDesktop) return;
            if (this.hasScrolled) return;

            const focusOutOfHeader = document.activeElement.closest('header-component') === null;
            const isSearchOpened = document.body.classList.contains('search-opened');
            const headerMenuOpened = this.classList.contains('site-header--menu-opened');

            if (isSearchOpened || headerMenuOpened) return;

            if (event.type === 'focusout' && !focusOutOfHeader) return;

            this.classList.remove('site-header--hovered');
            this.background.style.setProperty('--header-background-height', '0px');

            if (!isFocusEnabled) {
              document.activeElement.blur();
            }
          });
        }

        initMobileNavToggle() {
          this.mobileMenuTrigger?.addEventListener('click', (event) => {
            // ponytail: stops the click reaching document's outside-click-closes-drawer
            // listener, which doesn't recognize this button (no [data-drawer-toggle]) and
            // was closing the drawer in the same tick it just opened via the custom event.
            event.stopPropagation();

            if (!document.body.classList.contains('js-drawer-open')) {
              if (document.body.classList.contains('search-opened')) {
                document.addEventListener('theme:search:close', this.triggerDrawerOpeningEvent);
                return;
              }
              this.triggerDrawerOpening(this.mobileMenuTrigger);
            }
          });

          // ponytail: same reason as above, Drawer.close() only resets
          // aria-expanded on [data-drawer-toggle] buttons, so this trigger's
          // aria-expanded is stuck "true" after close without this.
          document.addEventListener('theme:drawer:close', () => {
            this.mobileMenuTrigger?.setAttribute('aria-expanded', false);
          });
        }

        triggerDrawerOpening(mobileMenuTrigger) {
          document.dispatchEvent(
            new CustomEvent('theme:drawer:opening', {
              bubbles: true,
              detail: {element: mobileMenuTrigger},
            })
          );
          document.removeEventListener('theme:search:close', this.triggerDrawerOpeningEvent);
        }

        _sumSplitWidths(nodes, alignment) {
          let arr = [];
          nodes.forEach((el) => {
            arr.push(el.clientWidth);
          });
          let [logoWidth, navWidth, iconsWidth] = arr;

          // Check if nav is left and set correct width
          if (alignment === 'left') {
            const tempWidth = logoWidth;
            logoWidth = navWidth;
            navWidth = tempWidth;
          }

          if (alignment !== 'right') {
            if (logoWidth > iconsWidth) {
              iconsWidth = logoWidth;
            } else {
              logoWidth = iconsWidth;
            }
          }

          return logoWidth + navWidth + iconsWidth;
        }

        rectsOverlap(rectOne, rectTwo) {
          return !(rectOne.right <= rectTwo.left || rectOne.left >= rectTwo.right || rectOne.bottom <= rectTwo.top || rectOne.top >= rectTwo.bottom);
        }

        disconnectedCallback() {
          // ponytail: getMinWidth() appends/removes a throwaway clone of this
          // element to measure it; without this guard, tearing the clone down
          // fires this same callback and strips js-drawer-open etc. off the
          // real document.body on every resize tick.
          if (this.classList.contains('js__header__clone')) return;

          this.resizeObserver?.disconnect();
          document.removeEventListener('theme:resize', this.checkWidthEvent);

          document.documentElement.style.removeProperty('--header-height');
          document.documentElement.style.removeProperty('--header-sticky-height');

          document.body.classList.remove(...['js-drawer-open', 'js-drawer-open-cart', 'js-quick-view-visible', 'js-quick-view-from-cart']);
          document.removeEventListener('theme:scroll', this.headerStateEvent);
          document.removeEventListener('theme:cart:close', this.updateBackgroundHeightEvent);
          document.removeEventListener('theme:search:close', this.updateBackgroundHeightEvent);
          document.body.removeEventListener('touchstart', this.handleTouchstartEvent);
        }
      }
    );
  }

  if (!customElements.get('hover-disclosure')) {
    customElements.define(
      'hover-disclosure',
      class HoverDisclosure extends HTMLElement {
        constructor() {
          super();
          this.key = this.getAttribute('aria-controls');
          this.link = this.querySelector('[data-top-link]');
          this.grandparent = this.classList.contains('grandparent');
          this.disclosure = document.getElementById(this.key);
          this.header = this.closest('[data-site-header]');
          this.background = document.querySelector('[data-header-background]');
          this.setBackgroundHeightEvent = () => this.setBackgroundHeight();
        }

        connectedCallback() {
          this.setAttribute('aria-haspopup', true);
          this.setAttribute('aria-expanded', false);
          this.setAttribute('aria-controls', this.key);

          this.connectHoverToggle();
          this.handleTablets();
        }

        setBackgroundHeight() {
          const hasScrolled = document.body.classList.contains('has-scrolled');
          const headerHeight = hasScrolled ? window.stickyHeaderHeight : this.header.offsetHeight;

          if (this.grandparent) {
            this.disclosure.style.height = 'auto';
            this.dropdownHeight = this.disclosure.offsetHeight + headerHeight;
          } else {
            this.dropdownHeight = headerHeight;
          }

          this.background.style.setProperty('--header-background-height', `${this.dropdownHeight}px`);

          // Hide header dropdowns on mobile
          if (window.innerWidth < theme.sizes.small) {
            this.hideDisclosure();
          }
        }

        showDisclosure() {
          if (this.isVisible) return;
          this.isVisible = true;

          this.setBackgroundHeight();
          document.addEventListener('theme:resize', this.setBackgroundHeightEvent);

          if (!this.header.classList.contains('site-header--compress')) {
            if (this.grandparent) {
              document.body.classList.add('megamenu-opened');
            }
            this.header.classList.add('site-header--menu-opened');
          }

          // Set accessibility and classes
          this.setAttribute('aria-expanded', true);
          this.classList.add('is-visible');
          this.disclosure.classList.add('is-visible');

          this.updateHeaderHover();
        }

        hideDisclosure() {
          if (!this.isVisible) return;
          this.isVisible = false;

          this.background.style.removeProperty('--header-background-height');
          document.removeEventListener('theme:resize', this.setBackgroundHeightEvent);

          this.classList.remove('is-visible');
          this.disclosure.classList.remove('is-visible');
          this.setAttribute('aria-expanded', false);
          this.header.classList.remove('site-header--menu-opened');
          document.body.classList.remove('megamenu-opened');
        }

        updateHeaderHover() {
          requestAnimationFrame(() => {
            const isHovered = this.header.matches(':hover');
            const hasHoveredClass = this.header.classList.contains('site-header--hovered');

            if (isHovered && !hasHoveredClass) this.header.classList.add('site-header--hovered');
          });
        }

        handleTablets() {
          // first click opens the popup, second click opens the link
          this.addEventListener('touchstart', (e) => {
            const isOpen = this.classList.contains('is-visible');
            if (!isOpen) {
              e.preventDefault();

              // Hide the rest of the active nav items
              const activeNavItems = this.header.querySelectorAll('.is-visible[data-nav-item]');

              if (activeNavItems.length > 0) {
                activeNavItems.forEach((item) => {
                  if (item !== this) {
                    item.dispatchEvent(new Event('mouseleave', {bubbles: true}));

                    const onTransitionEnd = () => {
                      requestAnimationFrame(() => {
                        this.showDisclosure();
                      });

                      item.removeEventListener('transitionend', onTransitionEnd);
                    };

                    item.addEventListener('transitionend', onTransitionEnd);
                  }
                });

                return;
              }

              this.showDisclosure();
            }
          });
        }

        connectHoverToggle() {
          this.addEventListener('mouseenter', () => this.showDisclosure());
          this.link.addEventListener('focus', () => this.showDisclosure());

          this.addEventListener('mouseleave', () => this.hideDisclosure());
          this.addEventListener('focusout', (e) => {
            const inMenu = this.contains(e.relatedTarget);

            if (!inMenu) {
              this.hideDisclosure();
            }
          });
          this.addEventListener('keyup', (e) => {
            if (e.code !== 'Escape') {
              return;
            }
            this.hideDisclosure();
          });
        }
      }
    );
  }
})();
